import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export interface ModelWithBrand extends Tables<'models'> {
  brand: Tables<'brands'>;
}

/**
 * Cached catalog of all active models with their brand joined client-side.
 * One light query shared across hooks so we don't repeat `models!inner(*, brands!inner(*))`
 * embedded joins on every variant fetch.
 */
export function useModelsCatalog() {
  return useQuery({
    queryKey: ['models-catalog'],
    staleTime: 5 * 60 * 1000, // 5 min
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const [{ data: models, error: mErr }, { data: brands, error: bErr }] = await Promise.all([
        supabase
          .from('models')
          .select('*')
          .eq('is_active', true)
          .eq('is_previous_generation', false),
        supabase.from('brands').select('*'),
      ]);
      if (mErr) throw new Error(mErr.message);
      if (bErr) throw new Error(bErr.message);

      const brandMap = new Map<string, Tables<'brands'>>();
      (brands || []).forEach((b) => brandMap.set(b.id, b));

      const result: ModelWithBrand[] = (models || [])
        .filter((m) => brandMap.has(m.brand_id))
        .map((m) => ({ ...m, brand: brandMap.get(m.brand_id)! }));

      const byId = new Map<string, ModelWithBrand>();
      result.forEach((m) => byId.set(m.id, m));

      return { models: result, byId, brandsById: brandMap };
    },
  });
}
